import { Tabs, TabsContent, TabsList, TabsTrigger } from '@atrvd/saas-ui'
import type { BalanceImportTab } from '@/types/banking'
import type { ReactNode } from 'react'
import { IntacctBalanceTable } from './IntacctBalanceTable'

interface BalanceImportTabsProps {
  activeTab: BalanceImportTab
  onTabChange: (tab: BalanceImportTab) => void
  children: ReactNode
  /**
   * Facility UUIDs to forward to the Intacct balance API import tab's
   * IntacctBalanceTable. When undefined or empty, the table shows all accounts.
   */
  facilityIds?: string[]
}

/**
 * Valid tab values for type-safe runtime validation
 */
const VALID_TABS: BalanceImportTab[] = [
  'balance-manual-update',
  'api-balance-import',
]

/**
 * Type guard for BalanceImportTab
 */
function isValidTab(value: string): value is BalanceImportTab {
  return VALID_TABS.includes(value as BalanceImportTab)
}

/**
 * Tab navigation for Balance Import page (DCP-267).
 *
 * Two tabs, in order: "Bank balance update" then "Intacct balance API import".
 * The inline-edit table (DCP-120) lives under "Bank balance update" — where the
 * AP-team workflow belongs; "Intacct balance API import" hosts the Intacct
 * Balances Import table (DCP-159/160). The empty "Balance update status" and
 * "Balance Import History" placeholder tabs are hidden until their user stories
 * land. (Internal tab values are unchanged — only labels/order.)
 *
 * @param facilityIds - Optional facility UUIDs forwarded to IntacctBalanceTable
 *   so the page-level facility filter applies to the Intacct balance API import tab.
 */
export function BalanceImportTabs({
  activeTab,
  onTabChange,
  children,
  facilityIds,
}: BalanceImportTabsProps) {
  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => {
        if (isValidTab(value)) {
          onTabChange(value)
        }
      }}
    >
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="balance-manual-update">Bank balance update</TabsTrigger>
        <TabsTrigger value="api-balance-import">Intacct balance API import</TabsTrigger>
      </TabsList>

      <TabsContent value="balance-manual-update" className="mt-6">
        {children}
      </TabsContent>

      <TabsContent value="api-balance-import" className="mt-6">
        {/* Intacct Balances Import (DCP-159/160) — self-contained container
            that fetches its own data and owns the per-account / Resync All
            sync controls. facilityIds propagates the page-level filter. */}
        <IntacctBalanceTable facilityIds={facilityIds} />
      </TabsContent>
    </Tabs>
  )
}
